import type { CSSProperties, ReactNode } from "react";
import styled from "styled-components";

export type ButtonProps = {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  onClick?: (e: Event) => void;
  variant?: "primary" | "secondary" | "outline" | "danger";
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  disabled?: boolean;
  fullWidth?: boolean;
  type?: "button" | "submit" | "reset";
  loading?: boolean;
  loadingText?: string;
};

//TODO: fix variants and put colors instead
const StyledButton = styled.button<Omit<ButtonProps, "children" | "icon">>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: ${(props) => (props.fullWidth ? "100%" : "auto")};

  ${({ variant = "primary" }) => {
    switch (variant) {
      case "primary":
        return `
          background-color: #000;
          color: #fff;
        `;
      case "secondary":
        return `
          background-color: #fff;
          color: #000;
        `;
      case "outline":
        return `
          background-color: transparent;
          color: #000;
          border: 1px solid #000;
        `;
      case "danger":
        return `
          background-color: #ff0000;
          color: #fff;
        `;
      default:
        return "";
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonContent = styled.div<{ $iconPosition?: "left" | "right" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: ${({ $iconPosition }) =>
    $iconPosition === "right" ? "row" : "row-reverse"};
`;

export const Button = ({
  children,
  onClick,
  variant = "primary",
  icon,
  iconPosition = "left",
  disabled = false,
  fullWidth,
  type = "button",
  style,
  className,
  loading = false,
  loadingText,
}: ButtonProps) => (
  <StyledButton
    className={className}
    onClick={onClick}
    variant={variant}
    disabled={disabled || loading}
    fullWidth={fullWidth}
    type={type}
    style={style}
  >
    {icon ? (
      <ButtonContent $iconPosition={iconPosition}>
        {icon}
        {loading && loadingText ? loadingText : children}
      </ButtonContent>
    ) : (
      loading && loadingText ? loadingText : children
    )}
  </StyledButton>
);
